import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Trash2, Minus, Plus, ArrowLeft, ArrowRight, MessageCircle, Package, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/contexts/AuthContext";
import { formatIDR } from "@/lib/format";
import { Modal } from "@/components/ui-kit";

/**
 * Iter v34b0 — ClientCart 3-step wizard.
 * Step 1: Cart items + qty controls
 * Step 2: Per-item config (location, PPN, bongkar)
 * Step 3: Rincian final + Hubungi Marketing (WhatsApp)
 */

// Iter v34b5 — Cache lazy image agar navigasi antar step tidak refetch
const _cartImgCache = new Map();
function CartItemImage({ item, size = "md" }) {
  const [b64, setB64] = React.useState(() => (item.image_variant_id ? _cartImgCache.get(item.image_variant_id) : null) || null);
  React.useEffect(() => {
    const id = item.image_variant_id;
    if (!id) { setB64(null); return; }
    const cached = _cartImgCache.get(id);
    if (cached) { setB64(cached); return; }
    let alive = true;
    api.get(`/prices/${id}/image`).then((r) => {
      const v = r.data?.image_b64 || null;
      if (v) _cartImgCache.set(id, v);
      if (alive) setB64(v);
    }).catch(() => { if (alive) setB64(null); });
    return () => { alive = false; };
  }, [item.image_variant_id]);
  const iconSize = size === "sm" ? "w-5 h-5" : "w-6 h-6";
  if (b64) return <img src={`data:image/png;base64,${b64}`} alt={item.name} className="w-full h-full object-cover" loading="lazy"/>;
  // legacy fallback (jika image_url data uri lama masih tersimpan)
  if (item.image_url && String(item.image_url).startsWith("data:")) {
    return <img src={item.image_url} alt={item.name} className="w-full h-full object-cover"/>;
  }
  return <Package className={`${iconSize} text-stone-400`}/>;
}

export default function ClientCart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, updateQty, updateItem, removeItem, clear, count } = useCart();
  const [step, setStep] = useState(1);
  const [waNumber, setWaNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  // Iter v34b6 — Template pesan WA (dapat dikonfigurasi di Database Settings)
  const [waTemplate, setWaTemplate] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setWaNumber(r.data.whatsapp_marketing || "");
      setCompanyName(r.data.company_name || "");
      setWaTemplate(r.data.wa_marketing_template || "");
    }).catch(() => {});
  }, []);

  // Iter v34cj — Formula baru:
  // - non PPN: base + ongkir + bongkar (per unit) × qty = subtotal
  // - with PPN: subtotal_with_costs × 11% = ppn; grand = subtotal_with_costs + ppn
  // - price_ex_vat sudah = harga_with_ppn / 1.11 (dari master price)
  // Bongkar diterapkan bila toggle "Biaya Bongkar" = Ya (default Ya) dan variant punya biaya.
  const rincians = useMemo(() => items.map((it) => {
    const variant = (it.variants || [])[it.selected_loc] || (it.variants || [])[0] || {};
    const q = Math.max(1, Number(it.qty) || 1);
    const base = it.tax_mode === "with_vat"
      ? (Number(variant.price_ex_vat) || 0)
      : (Number(variant.price_non_vat) || 0);
    const ongkirUnit = Number(variant.delivery_cost) || 0;
    const bongkarUnit = it.use_bongkar === false ? 0 : (Number(variant.unloading_fee) || 0);
    // Effective per-unit price includes ongkir + bongkar (absorbed silently)
    const perUnit = base + ongkirUnit + bongkarUnit;
    const subtotal = perUnit * q;
    const ppn = it.tax_mode === "with_vat" ? subtotal * 0.11 : 0;
    // Retain individual costs for WA breakdown (internal); not displayed in UI.
    const ongkir = ongkirUnit * q;
    const bongkar = bongkarUnit * q;
    const total = subtotal + ppn;
    return { it, variant, q, base, perUnit, subtotal, ppn, ongkir, bongkar, total };
  }), [items]);

  const grandTotal = rincians.reduce((s, r) => s + r.total, 0);

  const buildWhatsAppText = () => {
    const clientName = user?.client_name || user?.name || "Klien";
    const dateStr = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    // Iter v34b6 — Build blok "items" (dipakai placeholder {items} di template).
    const itemsLines = [];
    rincians.forEach((r, i) => {
      const loc = r.variant.delivery_location || "-";
      itemsLines.push(`${i + 1}. *${r.it.name}*`);
      itemsLines.push(`   Lokasi: ${loc}`);
      itemsLines.push(`   Qty: ${r.q} ${r.it.unit || "unit"}`);
      itemsLines.push(`   Harga/unit: ${formatIDR(r.base)} ${r.it.tax_mode === "with_vat" ? "(Exc PPN)" : ""}`);
      itemsLines.push(`   Subtotal: ${formatIDR(r.subtotal)}`);
      if (r.it.tax_mode === "with_vat") itemsLines.push(`   PPN 11%: ${formatIDR(r.ppn)}`);
      itemsLines.push(`   Ongkos Kirim: ${formatIDR(r.ongkir)}`);
      if (r.it.use_bongkar) itemsLines.push(`   Biaya Bongkar: ${formatIDR(r.bongkar)}`);
      itemsLines.push(`   *Total: ${formatIDR(r.total)}*`);
      if (i < rincians.length - 1) itemsLines.push("");
    });
    const itemsBlock = itemsLines.join("\n");
    const tpl = waTemplate ||
`*Permintaan Penawaran*
Kepada: {company_name}
Dari: {client_name}
Tanggal: {date}

*Rincian Pesanan:*
{items}

*GRAND TOTAL: {grand_total}*

Mohon konfirmasi ketersediaan & jadwal pengirimannya. Terima kasih.`;
    return tpl
      .replaceAll("{client_name}", clientName)
      .replaceAll("{company_name}", companyName || "Tim Marketing")
      .replaceAll("{date}", dateStr)
      .replaceAll("{items}", itemsBlock)
      .replaceAll("{grand_total}", formatIDR(grandTotal));
  };

  const openWhatsApp = () => {
    if (!waNumber) {
      toast.error("Nomor WhatsApp Marketing belum diatur oleh admin. Mohon hubungi admin.");
      return;
    }
    // Iter v34b3 (fix) — Normalize defensively: strip non-digits; 0xxx → 62xxx; 8xxx → 62xxx
    let cleaned = String(waNumber).replace(/[^\d]/g, "");
    if (cleaned.startsWith("0")) cleaned = "62" + cleaned.slice(1);
    else if (cleaned.startsWith("8")) cleaned = "62" + cleaned;
    if (!cleaned.startsWith("62")) cleaned = "62" + cleaned;
    if (cleaned.length < 10) {
      toast.error(`Nomor WhatsApp tidak valid: ${waNumber}. Minta admin perbaiki di Pengaturan.`);
      return;
    }
    const text = encodeURIComponent(buildWhatsAppText());
    window.open(`https://wa.me/${cleaned}?text=${text}`, "_blank");
  };

  if (count === 0 && step === 1) {
    return (
      <div className="min-h-full bg-[#fafaf5] p-4 md:p-8">
        <div className="max-w-2xl mx-auto text-center py-24" data-testid="cart-empty">
          <ShoppingCart className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h2 className="font-display text-2xl text-stone-800 mb-2">Keranjang Anda Kosong</h2>
          <p className="text-sm text-stone-500 mb-6">Tambahkan material dari katalog untuk mendapatkan estimasi harga & hubungi marketing.</p>
          <button
            onClick={() => navigate("/client/catalog")}
            className="px-6 py-3 rounded-full bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium transition"
            data-testid="cart-empty-goto-catalog"
          >
            Kembali ke Katalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#fafaf5] p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-emerald-800 text-white p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-emerald-400/20 blur-3xl"></div>
          <div className="relative flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-blue-200 font-semibold">Keranjang · Estimasi Harga</p>
              <h1 className="font-display text-3xl md:text-4xl mt-1">Rangkuman Pesanan</h1>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <StepBadge n={1} label="Keranjang" active={step === 1} done={step > 1} />
            <div className="h-px bg-blue-300/40 flex-1"></div>
            <StepBadge n={2} label="Konfigurasi" active={step === 2} done={step > 2} />
            <div className="h-px bg-blue-300/40 flex-1"></div>
            <StepBadge n={3} label="Rincian" active={step === 3} done={false} />
          </div>
        </div>

        {step === 1 && (
          <CartStep
            items={items}
            onUpdateQty={updateQty}
            onRemove={removeItem}
            onNext={() => setStep(2)}
            onBack={() => navigate("/client/catalog")}
          />
        )}
        {step === 2 && (
          <ConfigStep
            items={items}
            onUpdate={updateItem}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <RincianStep
            rincians={rincians}
            grandTotal={grandTotal}
            onBack={() => setStep(2)}
            onWhatsApp={openWhatsApp}
            onClear={() => { clear(); navigate("/client/catalog"); toast.success("Keranjang dikosongkan"); }}
            waAvailable={!!waNumber}
          />
        )}
      </div>
    </div>
  );
}

function StepBadge({ n, label, active, done }) {
  const cls = done ? "bg-emerald-400 text-blue-900"
    : active ? "bg-white text-blue-800"
    : "bg-blue-500/40 text-blue-100";
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${cls}`} data-testid={`cart-step-${n}`}>
      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-bold">{n}</span>
      <span className="text-[11px] uppercase tracking-wider font-semibold hidden md:inline">{label}</span>
    </div>
  );
}

// ============= STEP 1 =============
function CartStep({ items, onUpdateQty, onRemove, onNext, onBack }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-stone-200/70 rounded-3xl overflow-hidden" data-testid="cart-list">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-4 border-b border-stone-100 last:border-b-0" data-testid={`cart-item-${it.id}`}>
            <div className="w-16 h-16 rounded-xl bg-stone-100 flex items-center justify-center overflow-hidden shrink-0">
              <CartItemImage item={it} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-stone-900 truncate">{it.name}</div>
              <div className="text-xs text-stone-500">{it.category || "-"} · per {it.unit || "unit"}</div>
            </div>
            <QtyStepper value={it.qty} onChange={(v) => onUpdateQty(it.id, v)} testid={`cart-qty-${it.id}`} />
            <button
              onClick={() => onRemove(it.id)}
              className="text-stone-400 hover:text-red-600 p-2 transition"
              title="Hapus"
              data-testid={`cart-remove-${it.id}`}
            >
              <Trash2 className="w-4 h-4"/>
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-700 text-sm hover:bg-stone-50 transition"
          data-testid="cart-back-to-catalog"
        >
          <ArrowLeft className="h-4 w-4"/> Kembali ke Katalog
        </button>
        <button
          onClick={onNext}
          disabled={items.length === 0}
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium transition disabled:opacity-50"
          data-testid="cart-next-step2"
        >
          Lanjutkan <ArrowRight className="h-4 w-4"/>
        </button>
      </div>
    </div>
  );
}

function QtyStepper({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(Math.max(1, (Number(value) || 1) - 1))}
        className="w-9 h-9 rounded-full border border-stone-300 hover:border-blue-500 hover:text-blue-700 flex items-center justify-center transition"
        data-testid={`${testid}-dec`}
      >
        <Minus className="w-3.5 h-3.5"/>
      </button>
      <input
        type="number"
        min="1"
        value={value}
        onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
        onFocus={(e) => e.target.select()}
        className="w-14 text-center border border-stone-300 rounded-full px-2 py-1.5 text-sm font-mono-num focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        data-testid={`${testid}-input`}
      />
      <button
        onClick={() => onChange((Number(value) || 1) + 1)}
        className="w-9 h-9 rounded-full border border-stone-300 hover:border-blue-500 hover:text-blue-700 flex items-center justify-center transition"
        data-testid={`${testid}-inc`}
      >
        <Plus className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}

// ============= STEP 2 =============
function ConfigStep({ items, onUpdate, onNext, onBack }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-stone-200/70 rounded-3xl overflow-hidden" data-testid="config-list">
        {items.map((it) => (
          <div key={it.id} className="p-4 md:p-5 border-b border-stone-100 last:border-b-0 grid grid-cols-1 md:grid-cols-4 gap-4 items-start" data-testid={`config-item-${it.id}`}>
            <div className="md:col-span-1">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden shrink-0">
                  <CartItemImage item={it} size="sm" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 text-sm truncate">{it.name}</div>
                  <div className="text-xs text-stone-500">Qty: {it.qty} {it.unit || "unit"}</div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">Lokasi Pengiriman</label>
              <select
                value={it.selected_loc}
                onChange={(e) => onUpdate(it.id, { selected_loc: Number(e.target.value) })}
                className="w-full border border-stone-300 rounded-lg px-2 py-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                data-testid={`config-loc-${it.id}`}
              >
                {(it.variants || []).map((v, i) => (
                  <option key={v.id || i} value={i}>{v.delivery_location || "Tanpa Lokasi"}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">PPN</label>
              <div className="flex items-center gap-2 pt-1">
                <ToggleChip
                  active={it.tax_mode === "with_vat"}
                  onClick={() => onUpdate(it.id, { tax_mode: "with_vat" })}
                  testid={`config-ppn-yes-${it.id}`}
                >Ya (11%)</ToggleChip>
                <ToggleChip
                  active={it.tax_mode === "non_vat"}
                  onClick={() => onUpdate(it.id, { tax_mode: "non_vat" })}
                  testid={`config-ppn-no-${it.id}`}
                >Tidak</ToggleChip>
              </div>
            </div>
            {/* Iter v34db — Toggle "Biaya Bongkar Ya/Tidak" dikembalikan. */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">Biaya Bongkar</label>
              <div className="flex items-center gap-2 pt-1">
                <ToggleChip
                  active={it.use_bongkar !== false}
                  onClick={() => onUpdate(it.id, { use_bongkar: true })}
                  testid={`config-bongkar-yes-${it.id}`}
                >Ya</ToggleChip>
                <ToggleChip
                  active={it.use_bongkar === false}
                  onClick={() => onUpdate(it.id, { use_bongkar: false })}
                  testid={`config-bongkar-no-${it.id}`}
                >Tidak</ToggleChip>
              </div>
              <div className="text-[10px] text-stone-500 mt-1 font-mono-num" data-testid={`config-bongkar-info-${it.id}`}>
                {Number(((it.variants || [])[it.selected_loc] || {}).unloading_fee) > 0
                  ? `${formatIDR(Number(((it.variants || [])[it.selected_loc] || {}).unloading_fee))} / ${it.unit || "unit"}`
                  : "Tidak ada biaya bongkar di lokasi ini"}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-700 text-sm hover:bg-stone-50 transition" data-testid="config-back-step1">
          <ArrowLeft className="h-4 w-4"/> Kembali
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-6 py-3 rounded-full bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium transition" data-testid="config-next-step3">
          Lanjutkan <ArrowRight className="h-4 w-4"/>
        </button>
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs transition ${
        active ? "bg-blue-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

// ============= STEP 3 =============
function RincianStep({ rincians, grandTotal, onBack, onWhatsApp, onClear, waAvailable }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-stone-200/70 rounded-3xl overflow-hidden" data-testid="rincian-list">
        {rincians.map((r, i) => (
          <div key={r.it.id} className="p-5 border-b border-stone-100 last:border-b-0" data-testid={`rincian-item-${r.it.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center text-sm font-bold">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 truncate">{r.it.name}</div>
                <div className="text-xs text-stone-500">
                  {r.variant.delivery_location || "-"} · Qty {r.q} {r.it.unit || "unit"} · {r.it.tax_mode === "with_vat" ? "PPN 11%" : "Non PPN"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">Total</div>
                <div className="font-display text-lg text-blue-800 font-mono-num">{formatIDR(r.total)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <RincianCell label={`Qty ${r.it.unit || "unit"}`} value={r.q} raw/>
              <RincianCell label={`Harga${r.it.tax_mode === "with_vat" ? " (Exc PPN)" : " (Non PPN)"}`} value={r.perUnit} />
              <RincianCell label="Subtotal" value={r.subtotal} />
              {r.it.tax_mode === "with_vat" && <RincianCell label="PPN 11%" value={r.ppn} />}
            </div>
          </div>
        ))}
        <div className="p-5 bg-gradient-to-r from-blue-50 to-emerald-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Grand Total</div>
            <div className="text-[10px] text-stone-500">Estimasi harga · hubungi marketing untuk penawaran resmi</div>
          </div>
          <div className="font-display text-3xl text-blue-800 font-mono-num" data-testid="rincian-grand-total">{formatIDR(grandTotal)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          onClick={onBack}
          className="col-span-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-700 text-sm hover:bg-stone-50 transition"
          data-testid="rincian-back-step2"
        >
          <ArrowLeft className="h-4 w-4"/> Kembali
        </button>
        <button
          onClick={onClear}
          className="col-span-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-red-200 text-red-700 text-sm hover:bg-red-50 transition"
          data-testid="rincian-clear-cart"
        >
          <Trash2 className="h-4 w-4"/> Kosongkan
        </button>
        <button
          onClick={onWhatsApp}
          disabled={!waAvailable}
          className="col-span-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="rincian-whatsapp-btn"
          title={waAvailable ? "" : "Nomor WhatsApp Marketing belum diatur admin"}
        >
          <MessageCircle className="h-4 w-4"/> Hubungi Marketing
        </button>
      </div>
      {!waAvailable && (
        <div className="text-center text-[11px] text-stone-500" data-testid="wa-not-available">
          <Sparkles className="h-3 w-3 inline mr-1 text-amber-500"/> Nomor WhatsApp Marketing belum diatur. Mohon hubungi admin untuk mengaktifkan tombol ini.
        </div>
      )}
    </div>
  );
}

function RincianCell({ label, value, raw }) {
  return (
    <div className="rounded-lg bg-stone-50 px-3 py-2 border border-stone-100">
      <div className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      <div className="font-mono-num text-sm text-stone-800">{raw ? value : formatIDR(value)}</div>
    </div>
  );
}
